import { Injectable, inject } from '@angular/core';
import {
  Firestore,
  collection,
  doc,
  getDoc,
  setDoc,
  updateDoc,
  deleteDoc,
  query,
  getDocs,
  serverTimestamp,
  orderBy,
  addDoc
} from '@angular/fire/firestore';
import { Department, AuditLog } from '../models/user.model';
import { AuthService } from './auth.service';
import { HM_DEPARTMENT_ORDER, resolveHmDepartmentName } from '../data/hm-department-master';

@Injectable({
  providedIn: 'root'
})
export class DepartmentMasterService {
  private firestore = inject(Firestore);
  private authService = inject(AuthService);
  private deptCollection = collection(this.firestore, 'departments');

  async getDepartments(): Promise<Department[]> {
    const q = query(this.deptCollection, orderBy('name', 'asc'));
    const snapshot = await getDocs(q);
    const allowed = new Set(HM_DEPARTMENT_ORDER as readonly string[]);
    return snapshot.docs
      .map(d => ({ id: d.id, ...d.data() } as Department))
      .filter(d => {
        const canonical = resolveHmDepartmentName(d.name);
        return allowed.has(canonical) || allowed.has(d.name);
      })
      .map(d => ({
        ...d,
        name: resolveHmDepartmentName(d.name),
      }));
  }

  async addDepartment(name: string): Promise<void> {
    const user = this.authService.currentUser();
    const deptData = {
      name,
      isActive: true,
      createdAt: serverTimestamp(),
      history: [{
        timestamp: new Date(),
        action: 'Created',
        userId: user?.uid || 'system',
        details: `Department ${name} created`
      }]
    };
    await addDoc(this.deptCollection, deptData);
  }

  async updateDepartment(id: string, name: string): Promise<void> {
    const user = this.authService.currentUser();
    const deptDoc = doc(this.deptCollection, id);
    const snapshot = await getDoc(deptDoc);
    const history = snapshot.data()?.['history'] || [];
    
    await updateDoc(deptDoc, {
      name,
      updatedAt: serverTimestamp(),
      history: [...history, {
        timestamp: new Date(),
        action: 'Updated',
        userId: user?.uid || 'system',
        details: `Name changed to ${name}`
      }]
    });
  }

  async toggleStatus(id: string, isActive: boolean): Promise<void> {
    const user = this.authService.currentUser();
    const deptDoc = doc(this.deptCollection, id);
    const snapshot = await getDoc(deptDoc);
    const history = snapshot.data()?.['history'] || [];

    await updateDoc(deptDoc, {
      isActive,
      history: [...history, {
        timestamp: new Date(),
        action: isActive ? 'Activated' : 'Deactivated',
        userId: user?.uid || 'system',
        details: `Status changed to ${isActive ? 'Active' : 'Inactive'}`
      }]
    });
  }
}
